import React from 'react';

interface ModuleStatus {
  name: string;
  status: 'pass' | 'warn' | 'fail' | 'gap';
  score?: number;
}

interface OrgMosaicGridProps {
  orgName?: string;
  modules?: ModuleStatus[];
  size?: number; // px width, default 120
  showLabels?: boolean;
}

// Anchor-shape cell template with 22 unique module slots.
// Each slot is filled by a module from the data; extra slots show as 'gap'.
// The shape is: ring (crown, sides, bottom), connector, crossbar, shaft, flukes.
const ANCHOR_TEMPLATE: Array<{ row: number; col: number; slot: number }> = [
  // Top of ring (crown)
  { row: 0, col: 3, slot: 0 },
  { row: 0, col: 4, slot: 0 },
  // Ring row 1
  { row: 1, col: 2, slot: 1 },
  { row: 1, col: 3, slot: 2 },
  { row: 1, col: 4, slot: 2 },
  { row: 1, col: 5, slot: 3 },
  // Ring inner
  { row: 2, col: 2, slot: 4 },
  { row: 2, col: 5, slot: 5 },
  // Ring bottom
  { row: 3, col: 2, slot: 6 },
  { row: 3, col: 3, slot: 7 },
  { row: 3, col: 4, slot: 7 },
  { row: 3, col: 5, slot: 8 },
  // Connector (shaft top)
  { row: 4, col: 3, slot: 9 },
  { row: 4, col: 4, slot: 9 },
  // Crossbar (widest section)
  { row: 5, col: 0, slot: 10 },
  { row: 5, col: 1, slot: 11 },
  { row: 5, col: 2, slot: 12 },
  { row: 5, col: 3, slot: 13 },
  { row: 5, col: 4, slot: 14 },
  { row: 5, col: 5, slot: 15 },
  { row: 5, col: 6, slot: 16 },
  { row: 5, col: 7, slot: 17 },
  // Shaft
  { row: 6, col: 3, slot: 18 },
  { row: 6, col: 4, slot: 18 },
  // Flukes (bottom arms)
  { row: 7, col: 2, slot: 19 },
  { row: 7, col: 5, slot: 20 },
  { row: 8, col: 1, slot: 21 },
  { row: 8, col: 6, slot: 21 },
];

/**
 * Build the cell list for the given number of modules.
 * - If moduleCount <= 22, use the anchor shape but only populate the first moduleCount slots.
 * - Extra slots beyond moduleCount render as 'gap'.
 * - This keeps the iconic anchor silhouette while adapting to fewer modules.
 */
function buildCells(moduleCount: number): Array<{ row: number; col: number; moduleIndex: number }> {
  // Always use the anchor shape — slots beyond moduleCount will fall back to 'gap' in the renderer
  return ANCHOR_TEMPLATE.map((t) => ({
    row: t.row,
    col: t.col,
    moduleIndex: t.slot < moduleCount ? t.slot : -1, // -1 = gap placeholder
  }));
}

const STATUS_COLORS: Record<string, string> = {
  pass: '#00CED1',
  warn: '#F59E0B',
  fail: '#EF4444',
  gap: 'rgba(255,255,255,0.06)',
};

// Empty default — parent should pass real data from API
const DEFAULT_MODULES: ModuleStatus[] = [];

const OrgMosaicGrid: React.FC<OrgMosaicGridProps> = ({
  orgName,
  modules = DEFAULT_MODULES,
  size = 120,
  showLabels = false,
}) => {
  const cellSize = size / 9; // 9 columns for padding
  const cellGap = cellSize * 0.15;
  const totalRows = 9;
  const totalCols = 9;

  if (modules.length === 0) {
    return (
      <div className="inline-flex flex-col items-center">
        <div style={{ width: size, height: size }} className="flex items-center justify-center bg-white/[0.02] rounded-lg border border-white/[0.06]">
          <span className="text-[10px] text-white/20">No data</span>
        </div>
        {showLabels && <span className="text-[10px] text-white/40 mt-1 truncate max-w-[100px]">{orgName}</span>}
      </div>
    );
  }

  const cells = buildCells(modules.length);
  const GAP_MODULE: ModuleStatus = { name: 'Unused', status: 'gap' };

  return (
    <div className="inline-flex flex-col items-center">
      <svg
        width={size}
        height={size * (totalRows / totalCols)}
        viewBox={`0 0 ${totalCols * cellSize} ${totalRows * cellSize}`}
      >
        {cells.map((cell, i) => {
          const mod = cell.moduleIndex >= 0
            ? (modules[cell.moduleIndex] || GAP_MODULE)
            : GAP_MODULE;
          const color = STATUS_COLORS[mod.status];
          const opacity = mod.status === 'gap' ? 0.3 : mod.status === 'pass' ? 0.85 : 0.9;

          return (
            <g key={i}>
              <rect
                x={cell.col * cellSize + cellGap + cellSize * 0.5}
                y={cell.row * cellSize + cellGap}
                width={cellSize - cellGap * 2}
                height={cellSize - cellGap * 2}
                rx={cellSize * 0.12}
                fill={color}
                opacity={opacity}
              >
                <title>{mod.name}: {mod.status}{mod.score ? ` (${mod.score}%)` : ''}</title>
              </rect>
              {mod.status === 'pass' && (
                <rect
                  x={cell.col * cellSize + cellGap + cellSize * 0.5}
                  y={cell.row * cellSize + cellGap}
                  width={cellSize - cellGap * 2}
                  height={cellSize - cellGap * 2}
                  rx={cellSize * 0.12}
                  fill={color}
                  opacity={0.15}
                  style={{ filter: `blur(${cellSize * 0.3}px)` }}
                />
              )}
            </g>
          );
        })}
      </svg>
      {orgName && showLabels && (
        <span className="text-[10px] text-white/40 mt-1.5">{orgName}</span>
      )}
    </div>
  );
};

export default OrgMosaicGrid;
